import React, { useState, useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Drawer from '@mui/material/Drawer';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useRules } from '../context/RuleContext';
import { ruleSetToXml } from '../utils/xmlParser';
import { nonApplyingRulesContext, nonApplyingRulesReport } from '../utils/ruleShadowing';
import config from '../config';

// ============ CHAT HISTORY STORAGE ============
const HISTORY_KEY = 'gfs_ai_chat_history';
const HISTORY_EXPIRY_DAYS = 10;

function loadChatHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const history = JSON.parse(raw);
    const cutoff = Date.now() - HISTORY_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return history.filter(h => h.timestamp > cutoff);
  } catch { return []; }
}

function saveChatHistory(history) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}


// ============ UTILITY FUNCTIONS ============
function extractRuleFromResponse(text) {
  const cgMatch = text.match(/\{"carrierGroupAction"\s*:[\s\S]*?\}/);
  if (cgMatch) { try { const p = JSON.parse(cgMatch[0]); if (p.carrierGroupAction) return { __carrierGroupAction: p }; } catch {} }
  const bulkMatch = text.match(/\{"bulkAction"\s*:[\s\S]*?\}/);
  if (bulkMatch) { try { const p = JSON.parse(bulkMatch[0]); if (p.bulkAction) return { __bulkAction: p }; } catch {} }
  const codeBlockMatch = text.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
  if (codeBlockMatch) { 
    try { 
      const p = JSON.parse(codeBlockMatch[1].trim()); 
      if (p.carrierGroupAction) return { __carrierGroupAction: p }; 
      if (p.bulkAction) return { __bulkAction: p }; 
      if (p.rules && Array.isArray(p.rules)) return { __multiRules: p.rules };
      return p.rule || p; 
    } catch {} 
  }
  // Multi-rule array format
  const multiRuleMatch = text.match(/\{"rules"\s*:\s*\[[\s\S]*?\]\s*\}/);
  if (multiRuleMatch) { try { const p = JSON.parse(multiRuleMatch[0]); if (p.rules && Array.isArray(p.rules)) return { __multiRules: p.rules }; } catch {} }
  const jsonMatch = text.match(/\{"rule"\s*:\s*\{[\s\S]*?\}\s*\}/);
  if (jsonMatch) { try { return JSON.parse(jsonMatch[0]).rule; } catch {} }
  const objMatch = text.match(/\{[^{}]*"carrier"[^{}]*"sequenceNum"[^{}]*\}/);
  if (objMatch) { try { return JSON.parse(objMatch[0]); } catch {} }
  
  // FALLBACK: Try to extract rules from text description when AI doesn't include JSON
  // Look for patterns like "carrier=AC, minPrice=USD0, maxPrice=USD100, feePercent=-2"
  const textRules = [];
  const rulePattern = /carrier[=:]?\s*([A-Z0-9]{2})[,\s].*?(?:feePercent|FeePercent|fee\s*percent)[=:]?\s*(-?\d+\.?\d*)/gi;
  let match;
  while ((match = rulePattern.exec(text)) !== null) {
    const rule = { carrier: match[1], sequenceNum: 3, feePercent: parseFloat(match[2]), priceSource: 'GDS' };
    // Try to extract minPrice/maxPrice from nearby text
    const context = text.substring(Math.max(0, match.index - 10), Math.min(text.length, match.index + match[0].length + 100));
    const minMatch = context.match(/minPrice[=:]?\s*([A-Z]{3})(\d+)/i);
    const maxMatch = context.match(/maxPrice[=:]?\s*([A-Z]{3})(\d+)/i);
    if (minMatch) rule.minPrice = minMatch[1] + minMatch[2];
    if (maxMatch) rule.maxPrice = maxMatch[1] + maxMatch[2];
    textRules.push(rule);
  }
  if (textRules.length > 1) return { __multiRules: textRules };
  if (textRules.length === 1) return textRules[0];
  
  // FALLBACK 2: Look for structured list like "Rule 1: carrier=AC, ..."
  const simplePattern = /(?:Rule\s*\d+|For\s+([A-Z]{2}))[:\s].*?(?:Discount|Markup|fee)[:\s]*(-?\d+\.?\d*)%/gi;
  const simpleRules = [];
  let sMatch;
  while ((sMatch = simplePattern.exec(text)) !== null) {
    const carrier = sMatch[1] || text.substring(Math.max(0, sMatch.index - 30), sMatch.index).match(/([A-Z]{2})\*?\*?[:\s]*$/)?.[1];
    if (carrier && carrier.length === 2) {
      simpleRules.push({ carrier, sequenceNum: 3, feePercent: parseFloat(sMatch[2]) * (text.substring(sMatch.index, sMatch.index + sMatch[0].length).toLowerCase().includes('discount') ? -1 : 1), priceSource: 'GDS' });
    }
  }
  if (simpleRules.length > 1) return { __multiRules: simpleRules };
  if (simpleRules.length === 1) return simpleRules[0];
  
  return null;
}

function cleanAiMessage(text) {
  let c = text.replace(/```(?:json)?\s*\n?[\s\S]*?\n?```/g, '');
  c = c.replace(/\{"rules"\s*:\s*\[[\s\S]*?\]\s*\}/g, '');
  c = c.replace(/\{"rule"\s*:\s*\{[\s\S]*?\}\s*\}/g, '');
  c = c.replace(/\{"bulkAction"\s*:[\s\S]*?\}/g, '');
  c = c.replace(/\{"carrierGroupAction"\s*:[\s\S]*?\}/g, '');
  c = c.replace(/\{[^{}]*"carrier"[^{}]*"sequenceNum"[^{}]*\}/g, '');
  return c.replace(/\n{3,}/g, '\n\n').trim();
}


// ============ RULE PREVIEW ============
function RulePreview({ rule }) {
  if (!rule) return null;
  const sections = [
    { title: 'Basic', fields: [
      { label: 'Carrier', value: rule.carrier }, { label: 'Seq #', value: rule.sequenceNum },
      { label: 'Fee Amount', value: rule.feeAmount }, { label: 'Fee %', value: rule.feePercent != null ? `${rule.feePercent}%` : undefined },
      { label: 'Booking Fee', value: rule.oncePerBookingFeeAmount }, { label: 'Suppress', value: rule.suppress === 'true' ? 'Yes' : undefined },
    ]},
    { title: 'Route', fields: [
      { label: 'Origin', value: rule.origin }, { label: 'Destination', value: rule.destination },
      { label: 'Trip', value: rule.tripType }, { label: 'Cabin', value: rule.cabinClass },
      { label: 'Pax', value: rule.passengerType }, { label: 'Source', value: rule.priceSource },
    ]},
    { title: 'Dates', fields: [
      { label: 'Travel From', value: rule.travelDateEffective }, { label: 'Travel Until', value: rule.travelDateDiscontinue },
      { label: 'Ticket From', value: rule.ticketingDateEffective }, { label: 'Ticket Until', value: rule.ticketingDateDiscontinue },
    ]},
  ];
  return (
    <Card variant="outlined" sx={{ mt: 2, bgcolor: '#f8fdf8', border: '1px solid #c8e6c9', borderRadius: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <SmartToyIcon sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Generated Rule</Typography>
          <Chip label={`#${rule.sequenceNum}`} size="small" color="success" sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }} />
        </Box>
        {sections.map(s => {
          const vis = s.fields.filter(f => f.value != null && f.value !== '');
          if (!vis.length) return null;
          return (<Box key={s.title} sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.6rem', textTransform: 'uppercase' }}>{s.title}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
              {vis.map(f => <Chip key={f.label} label={`${f.label}: ${f.value}`} size="small" variant="outlined" sx={{ borderColor: '#a5d6a7', fontSize: '0.7rem', height: 22 }} />)}
            </Box>
          </Box>);
        })}
      </CardContent>
    </Card>
  );
}


// ============ SPLIT-SCREEN DIFF (SIDE-BY-SIDE) ============
function SplitDiffDialog({ open, onClose, rule, existingRule, feeRules, locationGroups, carrierGroups, testRuleSet, onApply, overrideAfterRules }) {
  const [editMode, setEditMode] = useState(false);
  const [editedXml, setEditedXml] = useState('');

  if (!rule && !overrideAfterRules) return null;
  const isUpdate = !!existingRule;

  // BEFORE XML
  const beforeXml = ruleSetToXml({ locationGroups, carrierGroups, feeRules, testRuleSet });
  const beforeLines = beforeXml.split('\n');

  // AFTER XML
  let afterRules;
  if (overrideAfterRules) {
    afterRules = overrideAfterRules;
  } else if (isUpdate) {
    afterRules = feeRules.map(r => r.sequenceNum === existingRule.sequenceNum ? rule : r).sort((a, b) => a.sequenceNum - b.sequenceNum);
  } else {
    const newRule = { ...rule, sequenceNum: 3 };
    const shifted = feeRules.map(r => r.sequenceNum >= 3 ? { ...r, sequenceNum: r.sequenceNum + 1 } : r);
    afterRules = [...shifted, newRule].sort((a, b) => a.sequenceNum - b.sequenceNum);
  }
  const computedAfterXml = ruleSetToXml({ locationGroups, carrierGroups, feeRules: afterRules, testRuleSet });

  // Use edited XML if in edit mode, otherwise computed
  const afterXml = editMode && editedXml ? editedXml : computedAfterXml;
  const afterLines = afterXml.split('\n');

  // Build side-by-side pairs
  const pairs = buildSideBySide(beforeLines, afterLines);
  const additions = pairs.filter(p => p.type === 'add').length;
  const deletions = pairs.filter(p => p.type === 'del').length;

  const handleEditToggle = () => {
    if (!editMode) { setEditedXml(computedAfterXml); }
    setEditMode(!editMode);
  };

  const handleApplyEdited = () => {
    if (editMode && editedXml) {
      // Apply the manually edited XML by loading it
      onApply(editedXml);
    } else {
      onApply();
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative', bgcolor: '#24292e' }}>
        <Toolbar variant="dense">
          <IconButton edge="start" color="inherit" onClick={onClose} size="small"><CloseIcon /></IconButton>
          <Typography sx={{ ml: 2, flex: 1, fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {overrideAfterRules ? 'Bulk Date Extension' : isUpdate ? `Update Rule #${existingRule.sequenceNum}` : `Add Rule #${rule.sequenceNum}`}
            <span style={{ marginLeft: 16, opacity: 0.7, fontSize: '0.8rem' }}>FinalAllTravomint.xml</span>
          </Typography>
          <Chip label={`+${additions}`} size="small" sx={{ color: '#3fb950', bgcolor: 'rgba(63,185,80,0.15)', fontFamily: 'monospace', mr: 0.5, fontWeight: 700 }} />
          <Chip label={`-${deletions}`} size="small" sx={{ color: '#f85149', bgcolor: 'rgba(248,81,73,0.15)', fontFamily: 'monospace', mr: 1, fontWeight: 700 }} />
          <Button color={editMode ? 'warning' : 'inherit'} variant={editMode ? 'contained' : 'outlined'} size="small"
            startIcon={<EditIcon />} onClick={handleEditToggle} sx={{ mr: 1 }}>
            {editMode ? 'Editing' : 'Edit'}
          </Button>
          <Button color="inherit" variant="outlined" size="small" onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button color="success" variant="contained" size="small" startIcon={<CheckCircleIcon />} onClick={handleApplyEdited}>
            {overrideAfterRules ? 'Apply Changes' : isUpdate ? 'Apply Update' : 'Apply Rule'}
          </Button>
        </Toolbar>
      </AppBar>
      {/* Column headers */}
      <Box sx={{ display: 'flex', bgcolor: '#2d333b', borderBottom: '1px solid #444c56' }}>
        <Box sx={{ flex: 1, px: 2, py: 0.5, borderRight: '1px solid #444c56' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#f85149' }}>
            ── Before (current)
          </Typography>
        </Box>
        <Box sx={{ flex: 1, px: 2, py: 0.5 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#3fb950' }}>
            ── After {editMode ? '(editing)' : '(proposed)'}
          </Typography>
        </Box>
      </Box>

      {editMode ? (
        /* Edit Mode: Before on left, editable textarea on right */
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left: Before (read-only) */}
          <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#1b1f23', borderRight: '1px solid #30363d' }}>
            <pre style={{ margin: 0, padding: 12, fontFamily: '"Consolas", monospace', fontSize: '0.75rem', color: '#adbac7', lineHeight: 1.5 }}>
              {beforeXml}
            </pre>
          </Box>
          {/* Right: Editable */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <textarea
              value={editedXml}
              onChange={(e) => setEditedXml(e.target.value)}
              spellCheck={false}
              style={{
                flex: 1, resize: 'none', border: 'none', outline: 'none', padding: 12,
                fontFamily: '"Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.5,
                backgroundColor: '#0d1117', color: '#7ee787', width: '100%',
              }}
            />
          </Box>
        </Box>
      ) : (
        /* Diff Mode: Side-by-side diff */
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
          {pairs.map((pair, i) => (
            <Box key={i} sx={{ display: 'flex', minHeight: 22, borderBottom: '1px solid #21262d' }}>
              {/* Left side (before) */}
              <Box sx={{
                flex: 1, display: 'flex', borderRight: '1px solid #30363d',
                bgcolor: pair.type === 'del' ? 'rgba(248,81,73,0.1)' : pair.type === 'change' ? 'rgba(248,81,73,0.06)' : 'transparent',
              }}>
                <Box sx={{ minWidth: 40, textAlign: 'right', px: 0.5, color: '#484f58', fontSize: '0.7rem', userSelect: 'none', borderRight: '1px solid #30363d', pt: '3px', fontFamily: 'monospace' }}>
                  {pair.leftNum || ''}
                </Box>
                <Box sx={{ flex: 1, px: 1, fontFamily: '"Consolas", monospace', fontSize: '0.75rem', whiteSpace: 'pre', overflow: 'hidden', pt: '2px', color: pair.type === 'del' || pair.type === 'change' ? '#ffa198' : '#adbac7' }}>
                  {pair.left || ''}
                </Box>
              </Box>
              {/* Right side (after) */}
              <Box sx={{
                flex: 1, display: 'flex',
                bgcolor: pair.type === 'add' ? 'rgba(63,185,80,0.1)' : pair.type === 'change' ? 'rgba(63,185,80,0.06)' : 'transparent',
              }}>
                <Box sx={{ minWidth: 40, textAlign: 'right', px: 0.5, color: '#484f58', fontSize: '0.7rem', userSelect: 'none', borderRight: '1px solid #30363d', pt: '3px', fontFamily: 'monospace' }}>
                  {pair.rightNum || ''}
                </Box>
                <Box sx={{ flex: 1, px: 1, fontFamily: '"Consolas", monospace', fontSize: '0.75rem', whiteSpace: 'pre', overflow: 'hidden', pt: '2px', color: pair.type === 'add' || pair.type === 'change' ? '#7ee787' : '#adbac7' }}>
                  {pair.right || ''}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  );
}

// Build side-by-side diff pairs using LCS
function buildSideBySide(oldLines, newLines) {
  const lcs = computeLCS(oldLines, newLines);
  const pairs = [];
  let oi = 0, ni = 0, li = 0;
  let oldNum = 1, newNum = 1;

  while (oi < oldLines.length || ni < newLines.length) {
    if (li < lcs.length && oi === lcs[li].aIdx && ni === lcs[li].bIdx) {
      pairs.push({ left: oldLines[oi], right: newLines[ni], leftNum: oldNum, rightNum: newNum, type: 'ctx' });
      oi++; ni++; oldNum++; newNum++; li++;
    } else {
      // Collect deletions and additions between LCS matches
      const dels = [];
      const adds = [];
      while (oi < oldLines.length && (li >= lcs.length || oi < lcs[li].aIdx)) {
        dels.push({ text: oldLines[oi], num: oldNum }); oi++; oldNum++;
      }
      while (ni < newLines.length && (li >= lcs.length || ni < lcs[li].bIdx)) {
        adds.push({ text: newLines[ni], num: newNum }); ni++; newNum++;
      }
      // Pair them side by side
      const max = Math.max(dels.length, adds.length);
      for (let i = 0; i < max; i++) {
        const d = dels[i];
        const a = adds[i];
        if (d && a) {
          pairs.push({ left: d.text, right: a.text, leftNum: d.num, rightNum: a.num, type: 'change' });
        } else if (d) {
          pairs.push({ left: d.text, right: '', leftNum: d.num, rightNum: '', type: 'del' });
        } else if (a) {
          pairs.push({ left: '', right: a.text, leftNum: '', rightNum: a.num, type: 'add' });
        }
      }
    }
  }
  return pairs;
}

function computeLCS(a, b) {
  const m = a.length, n = b.length;
  if (m > 1500 || n > 1500) { return simpleLCS(a, b); }
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++) dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1]+1 : Math.max(dp[i-1][j], dp[i][j-1]);
  const result = []; let i = m, j = n;
  while (i > 0 && j > 0) { if (a[i-1] === b[j-1]) { result.unshift({ aIdx: i-1, bIdx: j-1 }); i--; j--; } else if (dp[i-1][j] >= dp[i][j-1]) i--; else j--; }
  return result;
}

function simpleLCS(a, b) {
  const result = []; let j = 0;
  for (let i = 0; i < a.length && j < b.length; i++) { while (j < b.length && a[i] !== b[j]) j++; if (j < b.length && a[i] === b[j]) { result.push({ aIdx: i, bIdx: j }); j++; } }
  return result;
}


// Tolerates typos like "aplying" / "appling"
const NOT_APPLYING_INTENT = /(not|never|n't|nt)\s+(be\s+|getting\s+)?(ap+l?[iy]\w*|work\w*|reach\w*|match\w*|used)|\bblocked\b|shadow|unreachable|dead\s+rules?|unused\s+rules?|expired\s+rules?|\b(is|does|will)\s+rule\s*#?\s*\d+\s+(ap+l?[iy]\w*|work\w*)/i;

// ============ MAIN COMPONENT ============
export default function AiRuleChat({ onNavigate, onGoToAnalysis }) {
  const { feeRules, setFeeRules, locationGroups, carrierGroups, testRuleSet, addFeeRule, insertFeeRule, updateFeeRule, bulkExtendDates, updateCarrierGroup, loadFromXml } = useRules();
  const [chatHistory, setChatHistory] = useState(() => loadChatHistory());
  const [activeChat, setActiveChat] = useState(null); // null = new chat
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingRule, setPendingRule] = useState(null);
  const [pendingBulkAction, setPendingBulkAction] = useState(null);
  const [pendingCarrierGroupAction, setPendingCarrierGroupAction] = useState(null);
  const [pendingMultiRules, setPendingMultiRules] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [showBulkDiff, setShowBulkDiff] = useState(false);
  const [showMultiDiff, setShowMultiDiff] = useState(false);
  const [applied, setApplied] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, applied]);

  // Only treat as "existing" if the AI explicitly says to update (not for new rule creation)
  // New rules always insert at seq 3, they should never match an existing rule
  const existingRule = null; // AI chat always creates new rules, never updates

  // Save current messages to history
  const saveToHistory = useCallback((msgs) => {
    if (msgs.length === 0) return;
    const title = msgs.find(m => m.role === 'user')?.content?.slice(0, 50) || 'Untitled';
    const id = activeChat || Date.now().toString();
    const updated = chatHistory.filter(h => h.id !== id);
    updated.unshift({ id, title, messages: msgs, timestamp: Date.now() });
    setChatHistory(updated);
    saveChatHistory(updated);
    if (!activeChat) setActiveChat(id);
  }, [activeChat, chatHistory]);

  const loadChat = (chat) => {
    setActiveChat(chat.id);
    setMessages(chat.messages);
    setPendingRule(null); setPendingBulkAction(null); setPendingCarrierGroupAction(null); setPendingMultiRules(null);
    setShowDiff(false); setApplied(false);
    setDrawerOpen(false);
  };

  const deleteChat = (id) => {
    const updated = chatHistory.filter(h => h.id !== id);
    setChatHistory(updated);
    saveChatHistory(updated);
    if (activeChat === id) { setActiveChat(null); setMessages([]); }
  };

  const handleNewConversation = () => {
    setActiveChat(null); setMessages([]); setInput('');
    setPendingRule(null); setPendingBulkAction(null); setPendingCarrierGroupAction(null); setPendingMultiRules(null);
    setShowDiff(false); setApplied(false);
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    const userMessage = { role: 'user', content: trimmed };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setPendingRule(null); setPendingBulkAction(null); setPendingCarrierGroupAction(null); setPendingMultiRules(null);
    setShowDiff(false); setApplied(false);

    // "Which rules are not applying?" is answered from the computed analysis, not the AI,
    // so the result is always exact
    if (NOT_APPLYING_INTENT.test(trimmed)) {
      const seqMatch = /rule\s*(?:#|no\.?|number)?\s*(\d+)/i.exec(trimmed) || /#\s*(\d+)/.exec(trimmed);
      const carrierMatch = /\b([A-Z][A-Z0-9]|[0-9][A-Z])\b/.exec(trimmed);
      const reply = nonApplyingRulesReport(feeRules, carrierGroups, locationGroups, {
        onlySeq: seqMatch ? parseInt(seqMatch[1]) : undefined,
        onlyCarrier: !seqMatch && carrierMatch ? carrierMatch[1] : undefined,
      });
      const final = [...newMessages, { role: 'assistant', content: reply }];
      setMessages(final);
      saveToHistory(final);
      setLoading(false);
      return;
    }

    try {
      let existingRulesContext = '';
      if (feeRules.length > 0) {
        existingRulesContext = feeRules.slice(0, 50).map(r =>
          `#${r.sequenceNum}: carrier=${r.carrier}${r.origin ? ' o=' + r.origin : ''}${r.destination ? ' d=' + r.destination : ''}${r.feePercent != null ? ' %=' + r.feePercent : ''}${r.feeAmount ? ' amt=' + r.feeAmount : ''}${r.suppress ? ' SUPPRESS' : ''}`
        ).join('\n');
      }
      if (carrierGroups.length > 0) {
        existingRulesContext += '\n\nCARRIER GROUPS:\n' + carrierGroups.map(g => `#${g.tableNum}: [${g.carriers.join(',')}]`).join('\n');
      }
      if (feeRules.length > 0) {
        existingRulesContext += '\n\n' + nonApplyingRulesContext(feeRules, carrierGroups, locationGroups);
      }

      const res = await fetch(`${config.API_BASE}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages.map(m => ({ role: m.role, content: m.content })), existingRulesContext }),
      });
      const data = await res.json();
      if (data.success && data.reply) {
        const assistantMsg = { role: 'assistant', content: data.reply };
        const final = [...newMessages, assistantMsg];
        setMessages(final);
        saveToHistory(final);
        const extracted = extractRuleFromResponse(data.reply);
        if (extracted && extracted.__carrierGroupAction) setPendingCarrierGroupAction(extracted.__carrierGroupAction);
        else if (extracted && extracted.__bulkAction) setPendingBulkAction(extracted.__bulkAction);
        else if (extracted && extracted.__multiRules) setPendingMultiRules(extracted.__multiRules);
        else if (extracted && extracted.carrier && extracted.sequenceNum) setPendingRule(extracted);
      } else {
        const errMsg = { role: 'assistant', content: data.message || 'Error processing request.', isError: true };
        const final = [...newMessages, errMsg];
        setMessages(final); saveToHistory(final);
      }
    } catch {
      const errMsg = { role: 'assistant', content: 'Connection error. Backend not reachable.', isError: true };
      setMessages(prev => [...prev, errMsg]);
    } finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const handleConfirmRule = () => setShowDiff(true);

  const handleApplyRule = (editedXmlParam) => {
    // If manually edited XML is passed, load it directly
    if (typeof editedXmlParam === 'string') {
      const result = loadFromXml(editedXmlParam);
      setShowDiff(false); setShowBulkDiff(false); setApplied(true);
      if (result.success) {
        setMessages(prev => [...prev, { role: 'system', content: '✅ Manually edited XML applied successfully!' }]);
      } else {
        setMessages(prev => [...prev, { role: 'system', content: `❌ XML parse error: ${result.errors?.join(', ') || 'Unknown error'}` }]);
      }
      return;
    }

    if (pendingCarrierGroupAction) {
      const { carrierGroupAction, carrierCode, groupNumber } = pendingCarrierGroupAction;

      if (groupNumber === 'all') {
        // Remove from ALL carrier groups
        let removedCount = 0;
        carrierGroups.forEach(group => {
          if (group.carriers.some(c => c.toUpperCase() === carrierCode.toUpperCase())) {
            const newCarriers = group.carriers.filter(c => c.toUpperCase() !== carrierCode.toUpperCase());
            updateCarrierGroup(group.tableNum, newCarriers);
            removedCount++;
          }
        });
        setApplied(true);
        if (removedCount > 0) {
          setMessages(prev => [...prev, { role: 'system', content: `✅ Removed ${carrierCode} from ${removedCount} carrier group(s).` }]);
        } else {
          setMessages(prev => [...prev, { role: 'system', content: `⚠️ ${carrierCode} was not found in any carrier group.` }]);
        }
        return;
      }

      const group = carrierGroups.find(g => String(g.tableNum) === String(groupNumber));
      if (!group) { setMessages(prev => [...prev, { role: 'system', content: `❌ Group #${groupNumber} not found.` }]); return; }

      if (carrierGroupAction === 'addCarrier') {
        if (group.carriers.some(c => c.toUpperCase() === carrierCode.toUpperCase())) {
          setMessages(prev => [...prev, { role: 'system', content: `⚠️ ${carrierCode} is already in group #${groupNumber}.` }]);
        } else {
          updateCarrierGroup(group.tableNum, [...group.carriers, carrierCode.toUpperCase()]);
          setApplied(true);
          const affected = feeRules.filter(r => r.carrier === `U:${groupNumber}`);
          setMessages(prev => [...prev, { role: 'system', content: `✅ Added ${carrierCode} to Carrier Group #${groupNumber} (${pendingCarrierGroupAction.country || ''}).\n${affected.length} rule(s) using U:${groupNumber} will now also apply to ${carrierCode} flights.` }]);
        }
      } else {
        const newCarriers = group.carriers.filter(c => c.toUpperCase() !== carrierCode.toUpperCase());
        if (newCarriers.length === group.carriers.length) {
          setMessages(prev => [...prev, { role: 'system', content: `⚠️ ${carrierCode} not found in group #${groupNumber}.` }]);
        } else {
          updateCarrierGroup(group.tableNum, newCarriers);
          setApplied(true);
          setMessages(prev => [...prev, { role: 'system', content: `✅ Removed ${carrierCode} from Carrier Group #${groupNumber}.` }]);
        }
      }
      return;
    }
    if (pendingBulkAction) {
      if (pendingBulkAction.bulkAction === 'extendDates') {
        if (!showBulkDiff) {
          // First click: show diff
          setShowBulkDiff(true);
          return;
        }
        // Second click (from diff dialog): apply
        const days = pendingBulkAction.days || 2;
        // Add N days to each rule's existing dates
        setFeeRules(prev => prev.map(r => {
          const updated = { ...r };
          if (r.travelDateEffective) {
            const dt = new Date(r.travelDateEffective); dt.setDate(dt.getDate() + days);
            updated.travelDateEffective = dt.toISOString().split('T')[0];
          }
          if (r.travelDateDiscontinue) {
            const dt = new Date(r.travelDateDiscontinue); dt.setDate(dt.getDate() + days);
            updated.travelDateDiscontinue = dt.toISOString().split('T')[0];
          }
          if (r.ticketingDateEffective) {
            const dt = new Date(r.ticketingDateEffective); dt.setDate(dt.getDate() + days);
            updated.ticketingDateEffective = dt.toISOString().split('T')[0];
          }
          if (r.ticketingDateDiscontinue) {
            const dt = new Date(r.ticketingDateDiscontinue); dt.setDate(dt.getDate() + days);
            updated.ticketingDateDiscontinue = dt.toISOString().split('T')[0];
          }
          return updated;
        }));
        setShowBulkDiff(false);
        setApplied(true);
        setMessages(prev => [...prev, { role: 'system', content: `✅ Extended all existing dates by ${days} days (from their current values).` }]);
      }
      return;
    }
    if (!pendingRule) return;
    // Always insert new rules at sequence 3, shift everything at 3+ up
    const ruleToInsert = { ...pendingRule, sequenceNum: 3 };
    insertFeeRule(ruleToInsert);
    setShowDiff(false); setApplied(true);
    setMessages(prev => [...prev, { role: 'system', content: `✅ Rule #3 added! (existing rules shifted)` }]);
  };

  const suggestedPrompts = [
    // Market Strategy (highlighted)
    { category: '🌍 Market Strategy & AI Analysis', highlight: true, prompts: [
      "How to increase India traffic?",
      "Suggest airlines for US market",
      "What markets are we missing coverage in?",
      "Suggest seasonal rules for upcoming festivals",
      "Which airlines should we add for UK market?",
      "Analyze our Australia coverage and suggest improvements",
    ], analysisPrompts: [
      "Analyze rule file — insights, loopholes & market opportunities",
      "Full market analysis with seasonal recommendations",
    ]},
    // Date operations
    { category: '📅 Dates', prompts: [
      "Extend all rule dates by 2 days",
      "Extend all rule dates by 7 days",
      "Extend all rule dates by 30 days",
    ]},
    // Carrier group operations
    { category: '✈️ Carrier Groups', prompts: [
      "Add EK to India carrier group",
      "Remove PE from all carrier groups",
      "Remove AI from US carrier group",
      "Add QR to UAE carrier group",
    ]},
    // Create rules
    { category: '➕ Create Rules', prompts: [
      "Create a 5% discount for Lufthansa economy US to India",
      "Create a USD 3.00 flat fee for Air India one-way economy",
      "Create a suppress rule for EK business class round-trip",
      "Create a -2% discount for all GDS bookings from GB to US",
      "Create a once per booking fee of USD 5.00 for all carriers economy",
      "Create a suppress rule for all interline flights to India",
    ]},
    // Complex operations
    { category: '🔧 Modify & Query', prompts: [
      "Which rules are not applying?",
      "What rules apply to Emirates flights to India?",
      "Which carriers are in the India group?",
      "Show me all suppress rules",
      "Remove Air India rule",
      "What discount does Lufthansa get on US-India routes?",
    ]},
  ];

  const [showAllPrompts, setShowAllPrompts] = useState(false);


  return (
    <Box sx={{ height: 'calc(100vh - 140px)', display: 'flex', gap: 0 }}>
      {/* Chat History Sidebar - Always Visible */}
      <Box sx={{ width: 220, minWidth: 220, borderRight: '1px solid #e0e0e0', bgcolor: '#f7f7f8', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', color: 'text.secondary' }}>History</Typography>
          <IconButton size="small" onClick={handleNewConversation} sx={{ width: 24, height: 24 }}><AddIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {chatHistory.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>No chats yet</Typography></Box>
          )}
          {chatHistory.map(h => (
            <ListItemButton key={h.id} selected={activeChat === h.id} onClick={() => loadChat(h)}
              sx={{ py: 1, px: 1.5, borderBottom: '1px solid #eee', '&.Mui-selected': { bgcolor: '#e3f2fd' } }}>
              <ListItemText
                primary={h.title}
                secondary={new Date(h.timestamp).toLocaleDateString()}
                primaryTypographyProps={{ fontSize: '0.72rem', fontWeight: 500, noWrap: true }}
                secondaryTypographyProps={{ fontSize: '0.6rem' }}
              />
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteChat(h.id); }} sx={{ width: 20, height: 20 }}>
                <DeleteIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>Auto-deleted after 10 days</Typography>
        </Box>
      </Box>

      {/* Split Diff Dialog */}
      <SplitDiffDialog open={showDiff} onClose={() => setShowDiff(false)} rule={pendingRule}
        existingRule={existingRule} feeRules={feeRules} locationGroups={locationGroups}
        carrierGroups={carrierGroups} testRuleSet={testRuleSet} onApply={handleApplyRule} />

      {/* Bulk Diff Dialog (for date extension etc.) */}
      {showBulkDiff && pendingBulkAction && pendingBulkAction.bulkAction === 'extendDates' && (() => {
        const days = pendingBulkAction.days || 2;
        // Add N days to each rule's EXISTING dates (both start and end)
        const afterRules = feeRules.map(r => {
          const updated = { ...r };
          if (r.travelDateEffective) {
            const dt = new Date(r.travelDateEffective); dt.setDate(dt.getDate() + days);
            updated.travelDateEffective = dt.toISOString().split('T')[0];
          }
          if (r.travelDateDiscontinue) {
            const dt = new Date(r.travelDateDiscontinue); dt.setDate(dt.getDate() + days);
            updated.travelDateDiscontinue = dt.toISOString().split('T')[0];
          }
          if (r.ticketingDateEffective) {
            const dt = new Date(r.ticketingDateEffective); dt.setDate(dt.getDate() + days);
            updated.ticketingDateEffective = dt.toISOString().split('T')[0];
          }
          if (r.ticketingDateDiscontinue) {
            const dt = new Date(r.ticketingDateDiscontinue); dt.setDate(dt.getDate() + days);
            updated.ticketingDateDiscontinue = dt.toISOString().split('T')[0];
          }
          return updated;
        });
        return (
          <SplitDiffDialog open={true} onClose={() => setShowBulkDiff(false)}
            rule={null} existingRule={null}
            feeRules={feeRules} locationGroups={locationGroups}
            carrierGroups={carrierGroups} testRuleSet={testRuleSet}
            onApply={handleApplyRule}
            overrideAfterRules={afterRules} />
        );
      })()}

      {/* Multi-Rule Diff Dialog */}
      {showMultiDiff && pendingMultiRules && (() => {
        // Build the after rules by inserting all pending rules at seq 3+
        let afterRules = [...feeRules];
        pendingMultiRules.forEach((r, i) => {
          const newRule = { ...r, sequenceNum: 3 + i };
          afterRules = afterRules.map(existing => existing.sequenceNum >= (3 + i) ? { ...existing, sequenceNum: existing.sequenceNum + 1 } : existing);
          afterRules.push(newRule);
        });
        afterRules.sort((a, b) => a.sequenceNum - b.sequenceNum);
        return (
          <SplitDiffDialog open={true} onClose={() => setShowMultiDiff(false)}
            rule={null} existingRule={null}
            feeRules={feeRules} locationGroups={locationGroups}
            carrierGroups={carrierGroups} testRuleSet={testRuleSet}
            onApply={(editedXml) => {
              if (typeof editedXml === 'string') { loadFromXml(editedXml); }
              else { pendingMultiRules.forEach(r => { insertFeeRule({ ...r, sequenceNum: 3 }); }); }
              setShowMultiDiff(false); setApplied(true); setPendingMultiRules(null);
              setMessages(prev => [...prev, { role: 'system', content: `✅ ${pendingMultiRules.length} rules added successfully!` }]);
            }}
            overrideAfterRules={afterRules} />
        );
      })()}

      {/* Main Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', maxWidth: 900, mx: 'auto', width: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.1rem' }}>
              <SmartToyIcon color="primary" fontSize="small" /> AI Rule Assistant
            </Typography>
            <Typography variant="caption" color="text.secondary">Chat to create, modify, or remove rules</Typography>
          </Box>
          {messages.length > 0 && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleNewConversation} size="small">New</Button>
          )}
        </Box>

        {/* Messages */}
        <Paper sx={{ flex: 1, overflow: 'auto', p: 2, mb: 2, bgcolor: '#fafafa', borderRadius: 3, border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
          {messages.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: 2, pt: 3, overflow: 'auto' }}>
              <SmartToyIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.5 }} />
              <Typography variant="subtitle1" color="text.secondary">How can I help with fee rules?</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 500, textAlign: 'center' }}>
                I can create rules, modify dates, add/remove airlines from carrier groups, suppress routes, and more. Click any prompt below or type your own.
              </Typography>
              <Box sx={{ width: '100%', maxWidth: 750, mt: 1 }}>
                {suggestedPrompts.map((section, sIdx) => (
                  <Box key={sIdx} sx={{ mb: 2, ...(section.highlight ? { p: 1.5, borderRadius: 2, border: '1.5px solid', borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.04)', boxShadow: '0 0 8px rgba(25,118,210,0.1)' } : {}) }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: section.highlight ? 'primary.main' : 'text.secondary', fontSize: section.highlight ? '0.75rem' : '0.7rem', mb: 0.5, display: 'block' }}>
                      {section.category} {section.highlight && <Chip label="NEW" size="small" color="primary" sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }} />}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(showAllPrompts ? section.prompts : section.prompts.slice(0, section.highlight ? 3 : 2)).map((p, i) => (
                        <Chip key={i} label={p} onClick={() => { if (section.isAnalysis && onGoToAnalysis) { onGoToAnalysis(); } else { setInput(p); } }} variant={section.highlight ? 'filled' : 'outlined'} size="small"
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.main', color: 'white' }, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4, fontSize: '0.73rem' }, ...(section.highlight ? { bgcolor: section.isAnalysis ? 'rgba(156,39,176,0.08)' : 'rgba(25,118,210,0.08)', borderColor: section.isAnalysis ? 'secondary.main' : 'primary.main', color: section.isAnalysis ? 'secondary.main' : 'primary.main', fontWeight: 500 } : {}) }} />
                      ))}
                    </Box>
                    {section.analysisPrompts && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, pt: 1, borderTop: '1px dashed rgba(76,175,80,0.4)' }}>
                        {section.analysisPrompts.map((p, i) => (
                          <Chip key={`a-${i}`} label={`📊 ${p}`} onClick={() => { if (onGoToAnalysis) onGoToAnalysis(); }} variant="filled" size="small"
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#4caf50', color: 'white' }, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4, fontSize: '0.73rem' }, bgcolor: 'rgba(76,175,80,0.1)', color: '#2e7d32', fontWeight: 500 }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
                <Button size="small" onClick={() => setShowAllPrompts(!showAllPrompts)} sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                  {showAllPrompts ? 'Show Less' : 'Show All Prompts'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              {messages.map((msg, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  {msg.role === 'system' ? (
                    <Alert severity="success" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>{msg.content}</Alert>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1.5, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: msg.role === 'user' ? 'primary.main' : msg.isError ? 'error.main' : 'success.main' }}>
                        {msg.role === 'user' ? <PersonIcon sx={{ fontSize: 16 }} /> : <SmartToyIcon sx={{ fontSize: 16 }} />}
                      </Avatar>
                      <Paper elevation={0} sx={{ p: 1.5, maxWidth: '78%', borderRadius: 2, bgcolor: msg.role === 'user' ? 'primary.main' : msg.isError ? '#ffebee' : 'white', color: msg.role === 'user' ? 'white' : 'text.primary', border: msg.role === 'user' ? 'none' : '1px solid #e0e0e0' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.83rem' }}>
                          {msg.role === 'assistant' ? cleanAiMessage(msg.content) : msg.content}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Rule preview */}
                  {msg.role === 'assistant' && idx === messages.length - 1 && pendingRule && !applied && (
                    <Box sx={{ ml: 5, mt: 1 }}>
                      <RulePreview rule={pendingRule} />
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={handleConfirmRule} size="small">
                          Show Diff
                        </Button>
                        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => inputRef.current?.focus()} size="small">Modify</Button>
                        <Tooltip title="Copy JSON"><IconButton size="small" onClick={() => navigator.clipboard.writeText(JSON.stringify(pendingRule, null, 2))}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Box>
                  )}

                  {/* Multi-rule action */}
                  {msg.role === 'assistant' && idx === messages.length - 1 && pendingMultiRules && !applied && (
                    <Box sx={{ ml: 5, mt: 1 }}>
                      <Card variant="outlined" sx={{ bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AddCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {pendingMultiRules.length} Rules Ready to Create
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {pendingMultiRules.slice(0, 6).map((r, i) => (
                              <Chip key={i} label={`${r.carrier} ${r.feePercent > 0 ? '+' : ''}${r.feePercent}% ${r.minPrice || ''}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                            ))}
                            {pendingMultiRules.length > 6 && <Chip label={`+${pendingMultiRules.length - 6} more`} size="small" sx={{ fontSize: '0.7rem', height: 22 }} />}
                          </Box>
                        </CardContent>
                      </Card>
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                        <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} size="small"
                          onClick={() => setShowMultiDiff(true)}>
                          Show Diff
                        </Button>
                        <Button variant="outlined" size="small" onClick={() => setPendingMultiRules(null)}>Cancel</Button>
                        <Tooltip title="Copy JSON"><IconButton size="small" onClick={() => navigator.clipboard.writeText(JSON.stringify(pendingMultiRules, null, 2))}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Box>
                  )}

                  {/* Bulk action */}
                  {msg.role === 'assistant' && idx === messages.length - 1 && pendingBulkAction && !applied && (
                    <Box sx={{ ml: 5, mt: 1 }}>
                      <Card variant="outlined" sx={{ bgcolor: '#fff3e0', border: '1px solid #ffcc80' }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            <CompareArrowsIcon sx={{ fontSize: 14, mr: 0.5 }} />{pendingBulkAction.description || 'Bulk Action'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Affects all {feeRules.length} rules</Typography>
                        </CardContent>
                      </Card>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button variant="contained" color="warning" size="small" startIcon={<CompareArrowsIcon />} onClick={handleApplyRule}>Show Diff</Button>
                        <Button variant="outlined" size="small" onClick={() => setPendingBulkAction(null)}>Cancel</Button>
                      </Box>
                    </Box>
                  )}

                  {/* Carrier group action */}
                  {msg.role === 'assistant' && idx === messages.length - 1 && pendingCarrierGroupAction && !applied && (
                    <Box sx={{ ml: 5, mt: 1 }}>
                      <Card variant="outlined" sx={{ bgcolor: pendingCarrierGroupAction.carrierGroupAction === 'addCarrier' ? '#e8f5e9' : '#fce4ec', border: `1px solid ${pendingCarrierGroupAction.carrierGroupAction === 'addCarrier' ? '#a5d6a7' : '#f48fb1'}`, borderRadius: 2 }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="subtitle2" color={pendingCarrierGroupAction.carrierGroupAction === 'addCarrier' ? 'success.main' : 'error.main'} sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 1 }}>
                            {pendingCarrierGroupAction.carrierGroupAction === 'addCarrier' ? 'Add' : 'Remove'} {pendingCarrierGroupAction.carrierCode} {pendingCarrierGroupAction.carrierGroupAction === 'addCarrier' ? 'to' : 'from'} {pendingCarrierGroupAction.groupNumber === 'all' ? 'ALL Carrier Groups' : `Group #${pendingCarrierGroupAction.groupNumber} (${pendingCarrierGroupAction.country})`}
                          </Typography>
                          {(() => {
                            const isAll = pendingCarrierGroupAction.groupNumber === 'all';
                            const isAdd = pendingCarrierGroupAction.carrierGroupAction === 'addCarrier';
                            if (isAll) {
                              // Show all groups that contain this carrier
                              const groupsWithCarrier = carrierGroups.filter(g => g.carriers.some(c => c.toUpperCase() === pendingCarrierGroupAction.carrierCode.toUpperCase()));
                              return (<>
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'text.secondary' }}>FOUND IN {groupsWithCarrier.length} GROUP(S):</Typography>
                                  {groupsWithCarrier.map(g => (
                                    <Box key={g.tableNum} sx={{ mt: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>Group #{g.tableNum}:</Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.2 }}>
                                        {g.carriers.map(c => (
                                          <Chip key={c} label={c} size="small" sx={{ height: 18, fontSize: '0.6rem',
                                            ...(c.toUpperCase() === pendingCarrierGroupAction.carrierCode.toUpperCase() ? { bgcolor: '#f44336', color: 'white', textDecoration: 'line-through' } : {}) }} />
                                        ))}
                                      </Box>
                                    </Box>
                                  ))}
                                  {groupsWithCarrier.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{pendingCarrierGroupAction.carrierCode} not found in any group</Typography>}
                                </Box>
                              </>);
                            }
                            const grp = carrierGroups.find(g => String(g.tableNum) === String(pendingCarrierGroupAction.groupNumber));
                            const affected = feeRules.filter(r => r.carrier === `U:${pendingCarrierGroupAction.groupNumber}`);
                            return (<>
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'text.secondary' }}>CURRENT CARRIERS:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                                  {grp ? grp.carriers.map(c => (
                                    <Chip key={c} label={c} size="small" sx={{ height: 20, fontSize: '0.65rem',
                                      ...(!isAdd && c.toUpperCase() === pendingCarrierGroupAction.carrierCode.toUpperCase() ? { bgcolor: '#f44336', color: 'white', textDecoration: 'line-through' } : {}) }} />
                                  )) : <Typography variant="caption" color="error">Not found</Typography>}
                                  {isAdd && <Chip label={`+ ${pendingCarrierGroupAction.carrierCode}`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#4caf50', color: 'white', fontWeight: 700 }} />}
                                </Box>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Impact: {affected.length} rule(s) using U:{pendingCarrierGroupAction.groupNumber} will {isAdd ? 'now also apply to' : 'stop matching'} {pendingCarrierGroupAction.carrierCode}
                              </Typography>
                            </>);
                          })()}
                        </CardContent>
                      </Card>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button variant="contained" color={pendingCarrierGroupAction.carrierGroupAction === 'addCarrier' ? 'success' : 'error'} size="small" onClick={handleApplyRule}>
                          {pendingCarrierGroupAction.carrierGroupAction === 'addCarrier' ? `Add ${pendingCarrierGroupAction.carrierCode}` : `Remove ${pendingCarrierGroupAction.carrierCode}${pendingCarrierGroupAction.groupNumber === 'all' ? ' from All' : ''}`}
                        </Button>
                        <Button variant="outlined" size="small" onClick={() => setPendingCarrierGroupAction(null)}>Cancel</Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
              {loading && (
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ width: 30, height: 30, bgcolor: 'success.main' }}><SmartToyIcon sx={{ fontSize: 16 }} /></Avatar>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={14} /><Typography variant="caption">Thinking...</Typography></Box>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Paper>

        {/* Input */}
        <Paper sx={{ p: 1, borderRadius: 3, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField inputRef={inputRef} fullWidth multiline maxRows={3}
            placeholder="Describe the rule you want..." value={input}
            onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} disabled={loading}
            variant="standard" InputProps={{ disableUnderline: true }}
            sx={{ '& .MuiInputBase-root': { px: 1, py: 0.5, fontSize: '0.9rem' } }} />
          <IconButton onClick={sendMessage} disabled={!input.trim() || loading}
            sx={{ bgcolor: input.trim() ? 'primary.main' : '#e0e0e0', color: input.trim() ? 'white' : '#999', '&:hover': { bgcolor: input.trim() ? 'primary.dark' : '#e0e0e0' }, width: 36, height: 36 }}>
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Paper>
      </Box>
    </Box>
  );
}
